/**
 * GoAIWA Core JavaScript Storefront Engine
 * Handles: Wishlist, Recently Viewed, AJAX Add to Cart, Quick View, and Free Shipping Progress.
 */

// Global State
window.GoAIWA = window.GoAIWA || {};
window.GoAIWA.wishlist = JSON.parse(localStorage.getItem('goaiwa_wishlist')) || [];
window.GoAIWA.recentlyViewed = JSON.parse(localStorage.getItem('goaiwa_recently_viewed')) || [];

// Helper function to format price as "X.X QAR" or "X QAR"
function formatCustomPrice(priceCents) {
  if (!priceCents && priceCents !== 0) return '';
  const cents = priceCents % 100;
  if (cents === 0) {
    return `${Math.floor(priceCents / 100)} QAR`;
  } else {
    const tenths = Math.floor(cents / 10);
    const wholes = Math.floor(priceCents / 100);
    return `${wholes}.${tenths} QAR`;
  }
}

document.addEventListener('DOMContentLoaded', function() {
  // Initialize Core Functions
  initWishlist();
  initRecentlyViewed();
  initAJAXCart();
  initQuickView();
  initBackInStock();
  updateCartBubble();
  initCartDrawerObserver();
  initMobileProductCardTap();
  
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      setTimeout(() => {
        initCartDrawerObserver();
      }, 100);
    });
  }
  
  // Shopify Theme Editor compatibility
  document.addEventListener('shopify:section:load', function(e) {
    // Re-bind actions when section is loaded in customizer
    bindWishlistButtons();
    bindQuickViewButtons();
    initAJAXCart();
    initCartDrawerObserver();
  });
});

// ==========================================
// 1. WISHLIST SYSTEM (Hybrid Account & Local)
// ==========================================
function initWishlist() {
  // Merge account tags with localStorage if logged in
  if (window.isCustomerLoggedIn && window.customerWishlistTags) {
    window.customerWishlistTags.forEach(handle => {
      if (handle && !window.GoAIWA.wishlist.includes(handle)) {
        window.GoAIWA.wishlist.push(handle);
      }
    });
    localStorage.setItem('goaiwa_wishlist', JSON.stringify(window.GoAIWA.wishlist));
  }
  
  bindWishlistButtons();
  updateWishlistCount();
  initWishlistPage();
}

function bindWishlistButtons() {
  document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    
    // Set initial active state
    if (window.GoAIWA.wishlist.includes(handle)) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
    
    // Remove previous listeners to avoid duplicates
    btn.removeEventListener('click', handleWishlistClick);
    btn.addEventListener('click', handleWishlistClick);
  });
}

function handleWishlistClick(e) {
  e.preventDefault();
  e.stopPropagation();
  
  const btn = e.currentTarget;
  const handle = btn.getAttribute('data-product-handle');
  if (!handle) return;
  
  const index = window.GoAIWA.wishlist.indexOf(handle);
  if (index > -1) {
    // Remove from wishlist
    window.GoAIWA.wishlist.splice(index, 1);
    btn.classList.remove('active');
    
    // Animate and remove from wishlist page if currently on it
    const container = document.querySelector('[data-wishlist-container]');
    if (container) {
      const card = btn.closest('.wishlist-item-row') || btn.closest('.grid__item') || btn.closest('.bs-product-card');
      if (card) {
        card.style.opacity = '0';
        card.style.transform = 'scale(0.9)';
        card.style.transition = 'all 0.3s ease';
        setTimeout(() => {
          card.remove();
          if (window.GoAIWA.wishlist.length === 0) {
            container.innerHTML = '<div class="wishlist-empty-state"><p>Your wishlist is currently empty.</p><a href="/collections/all" class="button button--primary">Continue Shopping</a></div>';
            container.style.display = 'block';
          }
        }, 300);
      }
    }
  } else {
    // Add to wishlist
    window.GoAIWA.wishlist.push(handle);
    btn.classList.add('active');
  }
  
  localStorage.setItem('goaiwa_wishlist', JSON.stringify(window.GoAIWA.wishlist));
  updateWishlistCount();
  
  // Sync to customer account via AJAX contact form if logged in
  if (window.isCustomerLoggedIn) {
    syncWishlistToCustomerAccount();
  }
}

function initWishlistPage() {
  const container = document.querySelector('[data-wishlist-container]');
  if (!container) return;

  const wishlist = window.GoAIWA.wishlist;
  if (!wishlist || wishlist.length === 0) {
    container.innerHTML = '<div class="wishlist-empty-state"><p>Your wishlist is currently empty.</p><a href="/collections/all" class="button button--primary">Continue Shopping</a></div>';
    container.style.display = 'block';
    return;
  }

  container.innerHTML = `
    <div class="wishlist-card-container">
      <div class="wishlist-header-row">
        <h3>My Wishlist (${wishlist.length})</h3>
      </div>
      <div class="wishlist-items-list">
        ${Array(Math.min(wishlist.length, 3)).fill(`
          <div class="wishlist-skeleton-row">
            <div class="skeleton-element skeleton-image"></div>
            <div class="skeleton-element skeleton-info"></div>
            <div class="skeleton-element skeleton-delete"></div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
  container.style.display = 'block';

  const promises = wishlist.map(handle => 
    fetch(`/products/${handle}.js`)
      .then(res => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then(prod => buildWishlistItemRowHtml(prod))
      .catch(() => '')
  );

  Promise.all(promises).then(htmls => {
    const validHtmls = htmls.filter(h => h.trim() !== '');
    if (validHtmls.length === 0) {
      container.innerHTML = '<div class="wishlist-empty-state"><p>Your wishlist is currently empty.</p><a href="/collections/all" class="button button--primary">Continue Shopping</a></div>';
      return;
    }

    const listContainer = container.querySelector('.wishlist-items-list');
    if (listContainer) {
      listContainer.innerHTML = validHtmls.join('');
    }

    // Bind remove button events
    bindWishlistPageEvents();
  });
}

// Update badges homepage-wide
function updateWishlistCount() {
  const count = window.GoAIWA.wishlist.length;
  document.querySelectorAll('.wishlist-count-badge').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

function syncWishlistToCustomerAccount() {
  const tags = window.GoAIWA.wishlist.map(h => `wishlist_${h}`).join(',');
  
  const formData = new URLSearchParams();
  formData.append('form_type', 'customer');
  formData.append('utf8', '✓');
  formData.append('contact[email]', window.customerEmail);
  formData.append('contact[tags]', tags);
  
  fetch('/contact', {
    method: 'POST',
    body: formData,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }).catch(err => console.error('Error syncing wishlist to account:', err));
}

// ==========================================
// 2. RECENTLY VIEWED PRODUCTS
// ==========================================
function initRecentlyViewed() {
  const path = window.location.pathname;
  if (path.includes('/products/')) {
    const handle = path.split('/products/')[1].split('/')[0].split('?')[0];
    if (handle) {
      const index = window.GoAIWA.recentlyViewed.indexOf(handle);
      if (index > -1) {
        window.GoAIWA.recentlyViewed.splice(index, 1);
      }
      window.GoAIWA.recentlyViewed.unshift(handle);
      window.GoAIWA.recentlyViewed = window.GoAIWA.recentlyViewed.slice(0, 10);
      localStorage.setItem('goaiwa_recently_viewed', JSON.stringify(window.GoAIWA.recentlyViewed));
    }
  }
  
  renderRecentlyViewedSections();
}

function renderRecentlyViewedSections() {
  const containers = document.querySelectorAll('[data-recently-viewed-container]');
  if (containers.length === 0) return;
  
  let list = [...window.GoAIWA.recentlyViewed];
  const path = window.location.pathname;
  if (path.includes('/products/')) {
    const currentHandle = path.split('/products/')[1].split('/')[0].split('?')[0];
    list = list.filter(h => h !== currentHandle);
  }
  
  if (list.length === 0) {
    containers.forEach(c => {
      c.innerHTML = '<div class="rv-empty-state">No recently viewed products yet.</div>';
      c.closest('.recently-viewed-section-wrapper')?.classList.add('hidden');
    });
    return;
  }
  
  containers.forEach(container => {
    container.innerHTML = `
      <div class="rv-skeleton-wrapper" style="display: flex; gap: 20px; overflow: hidden; padding: 10px 5px;">
        ${Array(4).fill(`
          <div class="skeleton-card" style="flex: 1; min-width: 170px;">
            <div class="skeleton-element skeleton-image"></div>
            <div class="skeleton-element skeleton-title"></div>
            <div class="skeleton-element skeleton-price"></div>
          </div>
        `).join('')}
      </div>
    `;
    
    const promises = list.slice(0, 6).map(handle => 
      fetch(`/products/${handle}?view=card`)
        .then(res => {
          if (!res.ok) throw new Error('Card view not found');
          return res.text();
        })
        .catch(() => {
          return fetch(`/products/${handle}.js`)
            .then(res => res.json())
            .then(prod => buildFallbackProductCardHtml(prod))
            .catch(() => '');
        })
    );
    
    Promise.all(promises).then(htmls => {
      const validHtmls = htmls.filter(h => h.trim() !== '');
      if (validHtmls.length === 0) {
        container.innerHTML = '<div class="rv-empty-state">No items available.</div>';
        return;
      }
      
      container.innerHTML = `
        <div class="swiper rv-swiper">
          <div class="swiper-wrapper">
            ${validHtmls.map(html => `<div class="swiper-slide">${html}</div>`).join('')}
          </div>
          <div class="swiper-button-next"></div>
          <div class="swiper-button-prev"></div>
        </div>
      `;
      
      // Initialize Swiper carousel
      new Swiper(container.querySelector('.rv-swiper'), {
        slidesPerView: 4,
        spaceBetween: 20,
        navigation: {
          nextEl: container.querySelector('.swiper-button-next'),
          prevEl: container.querySelector('.swiper-button-prev'),
        },
        breakpoints: {
          320: { slidesPerView: 2, spaceBetween: 10 },
          768: { slidesPerView: 3, spaceBetween: 15 },
          1024: { slidesPerView: 4, spaceBetween: 20 }
        }
      });
      
      bindWishlistButtons();
      bindQuickViewButtons();
    });
  });
}

function buildFallbackProductCardHtml(product) {
  const price = formatCustomPrice(product.price);
  const comparePrice = product.compare_at_price ? formatCustomPrice(product.compare_at_price) : null;
  const image = product.featured_image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1.png';
  
  return `
    <div class="bs-product-card">
      <button class="card-wishlist-btn" data-wishlist-btn data-product-handle="${product.handle}" aria-label="Wishlist">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
      </button>
      <div class="bs-product-image" onclick="window.location.href='${product.url}'">
        <img src="${image}" alt="${product.title}" loading="lazy">
      </div>
      <div class="bs-product-info">
        <h4 class="bs-product-title" onclick="window.location.href='${product.url}'">${product.title}</h4>
        <div class="bs-product-price">
          <span class="bs-price">${price}</span>
          ${comparePrice ? `<span class="bs-compare-price">${comparePrice}</span>` : ''}
        </div>
        <button class="bs-cart-btn" data-quick-add-variant="${product.variants[0].id}" aria-label="Add to cart">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
        </button>
      </div>
    </div>
  `;
}

// ==========================================
// 3. AJAX ADD TO CART INTERCEPTOR
// ==========================================
function initAJAXCart() {
  document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
    if (form.closest('product-form')) return;
    if (form.dataset.ajaxCartBound) return;
    form.dataset.ajaxCartBound = 'true';

    form.addEventListener('submit', function(e) {
      if (form.getAttribute('data-no-ajax') === 'true') return;
      e.preventDefault();
      
      const submitBtn = form.querySelector('[type="submit"]');
      const originalBtnText = submitBtn ? submitBtn.innerHTML : 'Add to Cart';
      
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = 'Adding...';
      }
      
      const formData = new FormData(form);
      
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        drawer.getSectionsToRender().forEach((section) => {
          formData.append('sections', section.id);
        });
        formData.append('sections_url', window.location.pathname);
      }
      
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(res => {
        if (!res.ok) throw new Error('Failed to add product');
        return res.json();
      })
      .then(data => {
        if (submitBtn) {
          submitBtn.innerHTML = 'Added!';
          setTimeout(() => {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalBtnText;
          }, 2000);
        }
        
        refreshCartDrawer(data);
      })
      .catch(err => {
        console.error('AJAX add error:', err);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = 'Error Adding';
          setTimeout(() => { submitBtn.innerHTML = originalBtnText; }, 2000);
        }
      });
    });
  });

  document.querySelectorAll('[data-quick-add-variant]').forEach(btn => {
    btn.removeEventListener('click', handleQuickAddClick);
    btn.addEventListener('click', handleQuickAddClick);
  });
}

function handleQuickAddClick(e) {
  e.preventDefault();
  e.stopPropagation();
  
  const btn = e.currentTarget;
  const variantId = btn.getAttribute('data-quick-add-variant');
  if (!variantId) return;
  
  btn.disabled = true;
  
  const drawer = document.querySelector('cart-drawer');
  const sections = drawer && typeof drawer.getSectionsToRender === 'function'
    ? drawer.getSectionsToRender().map(s => s.id)
    : ['cart-drawer', 'cart-icon-bubble'];
  
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ 
      id: variantId, 
      quantity: 1,
      sections: sections
    })
  })
  .then(res => res.json())
  .then(data => {
    btn.disabled = false;
    refreshCartDrawer(data);
  })
  .catch(err => {
    btn.disabled = false;
    console.error('Quick add error:', err);
  });
}

function refreshCartDrawer(addedProduct) {
  updateCartBubble();
  
  const drawer = document.querySelector('cart-drawer');
  if (drawer && drawer.renderContents && addedProduct && addedProduct.sections) {
    drawer.renderContents(addedProduct);
  } else {
    fetch('/cart?section_id=cart-drawer')
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.getElementById('CartDrawer');
        const currentContent = document.getElementById('CartDrawer');
        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
          
          if (drawer) {
            drawer.classList.remove('is-empty');
            if (typeof drawer.open === 'function') {
              setTimeout(() => {
                const overlay = drawer.querySelector('#CartDrawer-Overlay');
                if (overlay) {
                  overlay.addEventListener('click', drawer.close.bind(drawer));
                }
                drawer.open();
              });
            } else {
              drawer.classList.add('animate', 'active');
              document.body.classList.add('overflow-hidden');
            }
          }
        }
      });
  }
}

function updateCartBubble() {
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      const bubbles = document.querySelectorAll('.ph-cart-count, .cart-count-bubble span, .mbn-cart-badge');
      bubbles.forEach(bubble => {
        bubble.textContent = cart.item_count;
        bubble.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
      
      updateFreeShippingProgress(cart.total_price);
      
      document.dispatchEvent(new CustomEvent('goaiwa:cart:updated', { detail: cart }));
    });
}

function updateFreeShippingProgress(totalPriceCents) {
  const limitCents = 9900; // QAR 99
  const percent = Math.min((totalPriceCents / limitCents) * 100, 100);
  
  document.querySelectorAll('.free-shipping-bar-fill').forEach(fill => {
    fill.style.width = `${percent}%`;
  });
  
  const remainingCents = limitCents - totalPriceCents;
  document.querySelectorAll('.free-shipping-text').forEach(el => {
    if (remainingCents > 0) {
      el.innerHTML = `Add <strong>${formatCustomPrice(remainingCents)}</strong> more for <strong>FREE DELIVERY</strong>`;
    } else {
      el.innerHTML = `🎉 You qualify for <strong>FREE DELIVERY</strong>!`;
    }
  });
}

// ==========================================
// 4. PRODUCT QUICK VIEW MODAL
// ==========================================
function initQuickView() {
  let modal = document.getElementById('goaiwa-quickview-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'goaiwa-quickview-modal';
    modal.className = 'qv-modal';
    modal.innerHTML = `
      <div class="qv-overlay"></div>
      <div class="qv-content">
        <button class="qv-close" aria-label="Close Quick View">&times;</button>
        <div class="qv-body"></div>
      </div>
    `;
    document.body.appendChild(modal);
    
    modal.querySelector('.qv-overlay').addEventListener('click', closeQuickView);
    modal.querySelector('.qv-close').addEventListener('click', closeQuickView);
  }
  
  bindQuickViewButtons();
}

function bindQuickViewButtons() {
  document.querySelectorAll('[data-quick-view-btn]').forEach(btn => {
    btn.removeEventListener('click', handleQuickViewClick);
    btn.addEventListener('click', handleQuickViewClick);
  });
}

function handleQuickViewClick(e) {
  e.preventDefault();
  e.stopPropagation();
  
  const handle = e.currentTarget.getAttribute('data-product-handle');
  if (!handle) return;
  
  openQuickView(handle);
}

function openQuickView(handle) {
  const modal = document.getElementById('goaiwa-quickview-modal');
  const body = modal.querySelector('.qv-body');
  body.innerHTML = '<div class="qv-loading">Loading product...</div>';
  modal.classList.add('active');
  document.body.classList.add('qv-open');
  
  fetch(`/products/${handle}.js`)
    .then(res => res.json())
    .then(product => {
      renderQuickViewProduct(product, body);
    })
    .catch(err => {
      console.error('Error loading quickview:', err);
      body.innerHTML = '<div class="qv-error">Failed to load product details.</div>';
    });
}

function closeQuickView() {
  const modal = document.getElementById('goaiwa-quickview-modal');
  if (modal) {
    modal.classList.remove('active');
    modal.querySelector('.qv-body').innerHTML = '';
  }
  document.body.classList.remove('qv-open');
}

function renderQuickViewProduct(product, container) {
  const images = product.images.length > 0 ? product.images : ['https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1.png'];
  const price = formatCustomPrice(product.price);
  const comparePrice = product.compare_at_price ? formatCustomPrice(product.compare_at_price) : null;
  
  const imageSlides = images.map(img => `
    <div class="swiper-slide">
      <img src="${img}" alt="${product.title}">
    </div>
  `).join('');
  
  const optionsHtml = product.options.map((option, optIdx) => {
    if (option.name === 'Title' && option.values.includes('Default Title')) return '';
    
    return `
      <div class="qv-option" data-option-index="${optIdx}">
        <label class="qv-option-label">${option.name}</label>
        <div class="qv-option-values">
          ${option.values.map((val, valIdx) => `
            <button class="qv-val-btn ${valIdx === 0 ? 'active' : ''}" data-value="${val}">${val}</button>
          `).join('')}
        </div>
      </div>
    `;
  }).join('');

  container.innerHTML = `
    <div class="qv-product-layout">
      <div class="qv-media">
        <div class="swiper qv-swiper">
          <div class="swiper-wrapper">${imageSlides}</div>
          <div class="swiper-pagination"></div>
          <div class="swiper-button-next"></div>
          <div class="swiper-button-prev"></div>
        </div>
      </div>
      
      <div class="qv-info">
        <h2 class="qv-title">${product.title}</h2>
        <div class="qv-price-wrapper">
          <span class="qv-price">${price}</span>
          ${comparePrice ? `<span class="qv-compare-price">${comparePrice}</span>` : ''}
        </div>
        
        <div class="qv-description">${product.description ? product.description.replace(/<[^>]*>/g, '').substring(0, 200) + '...' : ''}</div>
        
        <form class="qv-form" data-product-json='${JSON.stringify(product).replace(/'/g, "&#39;")}'>
          <input type="hidden" name="id" class="qv-variant-id" value="${product.variants[0].id}">
          
          <div class="qv-options">${optionsHtml}</div>
          
          <div class="qv-qty-add">
            <div class="qv-qty">
              <button type="button" class="qty-btn minus">-</button>
              <input type="number" name="quantity" value="1" min="1" class="qty-input">
              <button type="button" class="qty-btn plus">+</button>
            </div>
            <button type="submit" class="qv-add-btn" ${!product.available ? 'disabled' : ''}>
              ${product.available ? 'Add to Cart' : 'Sold Out'}
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
  
  new Swiper(container.querySelector('.qv-swiper'), {
    pagination: { el: '.swiper-pagination', clickable: true },
    navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }
  });
  
  initQuickViewVariantHandlers(container);
}

function initQuickViewVariantHandlers(container) {
  const form = container.querySelector('.qv-form');
  const product = JSON.parse(form.getAttribute('data-product-json'));
  const variantIdInput = form.querySelector('.qv-variant-id');
  const qvPrice = container.querySelector('.qv-price');
  const qvCompare = container.querySelector('.qv-compare-price');
  const qvAddBtn = container.querySelector('.qv-add-btn');
  
  const qtyInput = form.querySelector('.qty-input');
  form.querySelector('.qty-btn.minus').addEventListener('click', () => {
    qtyInput.value = Math.max(1, parseInt(qtyInput.value) - 1);
  });
  form.querySelector('.qty-btn.plus').addEventListener('click', () => {
    qtyInput.value = parseInt(qtyInput.value) + 1;
  });
  
  form.querySelectorAll('.qv-val-btn').forEach(btn => {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      const parent = btn.closest('.qv-option-values');
      parent.querySelectorAll('.qv-val-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      updateSelectedVariant();
    });
  });
  
  function updateSelectedVariant() {
    const selectedValues = [];
    form.querySelectorAll('.qv-option').forEach(opt => {
      const activeBtn = opt.querySelector('.qv-val-btn.active');
      if (activeBtn) {
        selectedValues.push(activeBtn.getAttribute('data-value'));
      }
    });
    
    const variant = product.variants.find(v => {
      return v.options.every((optVal, idx) => optVal === selectedValues[idx]);
    });
    
    if (variant) {
      variantIdInput.value = variant.id;
      qvPrice.textContent = formatCustomPrice(variant.price);
      if (variant.compare_at_price) {
        qvCompare.textContent = formatCustomPrice(variant.compare_at_price);
        qvCompare.style.display = 'inline-block';
      } else {
        qvCompare.style.display = 'none';
      }
      
      if (variant.available) {
        qvAddBtn.disabled = false;
        qvAddBtn.textContent = 'Add to Cart';
      } else {
        qvAddBtn.disabled = true;
        qvAddBtn.textContent = 'Sold Out';
      }
    } else {
      qvAddBtn.disabled = true;
      qvAddBtn.textContent = 'Unavailable';
    }
  }
  
  form.addEventListener('submit', function(e) {
    e.preventDefault();
    qvAddBtn.disabled = true;
    qvAddBtn.textContent = 'Adding...';
    
    const formData = new FormData(form);
    
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      drawer.getSectionsToRender().forEach((section) => {
        formData.append('sections', section.id);
      });
      formData.append('sections_url', window.location.pathname);
    }
    
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(res => res.json())
    .then(data => {
      qvAddBtn.textContent = 'Added!';
      setTimeout(() => {
        qvAddBtn.disabled = false;
        qvAddBtn.textContent = 'Add to Cart';
        closeQuickView();
      }, 1000);
      refreshCartDrawer(data);
    })
    .catch(err => {
      console.error(err);
      qvAddBtn.disabled = false;
      qvAddBtn.textContent = 'Add to Cart';
    });
  });
}

// ==========================================
// 5. BACK IN STOCK NOTIFICATIONS
// ==========================================
function initBackInStock() {
  let modal = document.getElementById('goaiwa-bis-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'goaiwa-bis-modal';
    modal.className = 'bis-modal';
    modal.innerHTML = `
      <div class="bis-overlay"></div>
      <div class="bis-content">
        <button class="bis-close" aria-label="Close Notify Form">&times;</button>
        <h3 class="bis-title">Notify Me When Available</h3>
        <p class="bis-subtitle">Get notified by email once this product comes back in stock.</p>
        <form class="bis-form">
          <input type="hidden" name="variant_id" class="bis-variant-id" value="">
          <input type="hidden" name="product_title" class="bis-product-title" value="">
          <input type="email" name="email" class="bis-email-input" placeholder="Enter your email" required>
          <button type="submit" class="bis-submit-btn">Send Notification Request</button>
        </form>
        <div class="bis-success-message" style="display:none;">🎉 We will email you once it becomes available!</div>
      </div>
    `;
    document.body.appendChild(modal);
    
    modal.querySelector('.bis-overlay').addEventListener('click', closeBISModal);
    modal.querySelector('.bis-close').addEventListener('click', closeBISModal);
    modal.querySelector('.bis-form').addEventListener('submit', handleBISSubmit);
  }
}

function openBISModal(variantId, productTitle) {
  const modal = document.getElementById('goaiwa-bis-modal');
  modal.querySelector('.bis-variant-id').value = variantId;
  modal.querySelector('.bis-product-title').value = productTitle;
  modal.querySelector('.bis-form').style.display = 'block';
  modal.querySelector('.bis-success-message').style.display = 'none';
  modal.classList.add('active');
}

function closeBISModal() {
  document.getElementById('goaiwa-bis-modal')?.classList.remove('active');
}

function handleBISSubmit(e) {
  e.preventDefault();
  const form = e.target;
  const submitBtn = form.querySelector('.bis-submit-btn');
  submitBtn.disabled = true;
  submitBtn.textContent = 'Submitting...';
  
  const email = form.querySelector('.bis-email-input').value;
  const variantId = form.querySelector('.bis-variant-id').value;
  const title = form.querySelector('.bis-product-title').value;
  
  const formData = new URLSearchParams();
  formData.append('form_type', 'customer');
  formData.append('utf8', '✓');
  formData.append('contact[email]', email);
  formData.append('contact[tags]', `back-in-stock_var-${variantId}`);
  formData.append('contact[body]', `Customer requested back-in-stock notification for product: ${title} (Variant ID: ${variantId})`);
  
  fetch('/contact', {
    method: 'POST',
    body: formData,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  })
  .then(() => {
    form.style.display = 'none';
    document.getElementById('goaiwa-bis-modal').querySelector('.bis-success-message').style.display = 'block';
    setTimeout(closeBISModal, 3000);
  })
  .catch(err => {
    console.error(err);
    submitBtn.disabled = false;
    submitBtn.textContent = 'Send Notification Request';
  });
}

window.GoAIWA.triggerBackInStock = function(variantId, productTitle) {
  openBISModal(variantId, productTitle);
};

// ==========================================
// 6. CART DRAWER OBSERVER & UPSELL RECOMMENDATIONS
// ==========================================
let cartDrawerObserver;

function initCartDrawerObserver() {
  const target = document.getElementById('CartDrawer-CartItems');
  if (!target) return;
  
  if (cartDrawerObserver) cartDrawerObserver.disconnect();
  
  cartDrawerObserver = new MutationObserver(() => {
    initCartUpsells();
  });
  
  cartDrawerObserver.observe(target, { childList: true });
  
  // Run once immediately on load
  initCartUpsells();
}

function initCartUpsells() {
  const container = document.querySelector('[data-cart-upsell-container]');
  if (!container) return;
  
  const parent = container.closest('.cart-drawer-upsell');
  if (!parent) return;
  
  const productId = parent.getAttribute('data-product-id');
  if (!productId) return;
  
  // Prevent infinite loop by checking if already loaded for this ID
  if (container.getAttribute('data-loaded-for-id') === productId) return;
  
  container.setAttribute('data-loaded-for-id', productId);
  container.innerHTML = '<div class="upsell-loading">Loading recommendations...</div>';
  
  fetch(`/recommendations/products.json?product_id=${productId}&limit=2`)
    .then(res => res.json())
    .then(data => {
      const products = data.products || [];
      if (products.length === 0) {
        parent.style.display = 'none';
        return;
      }
      
      parent.style.display = 'block';
      container.innerHTML = products.map(prod => {
        const price = formatCustomPrice(prod.price);
        const image = prod.featured_image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1.png';
        return `
          <div class="cart-upsell-item">
            <img src="${image}" alt="${prod.title}" class="upsell-item-img">
            <div class="upsell-item-info">
              <h5 class="upsell-item-title">${prod.title}</h5>
              <span class="upsell-item-price">${price}</span>
            </div>
            <button class="upsell-item-add-btn" data-quick-add-variant="${prod.variants[0].id}">
              Add
            </button>
          </div>
        `;
      }).join('');
      
      // Bind event listeners to new add buttons
      container.querySelectorAll('[data-quick-add-variant]').forEach(btn => {
        btn.addEventListener('click', handleQuickAddClick);
      });
    })
    .catch(err => {
      console.error('Error fetching cart upsells:', err);
      parent.style.display = 'none';
    });
}

function buildWishlistItemRowHtml(product) {
  const price = formatCustomPrice(product.price);
  const comparePrice = product.compare_at_price ? formatCustomPrice(product.compare_at_price) : null;
  const image = product.featured_image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1.png';
  
  // Calculate discount percentage
  let discountBadge = '';
  if (product.compare_at_price && product.compare_at_price > product.price) {
    const savings = product.compare_at_price - product.price;
    const percent = Math.round((savings / product.compare_at_price) * 100);
    discountBadge = `<span class="wishlist-discount">${percent}% off</span>`;
  }
  
  return `
    <div class="wishlist-item-row" data-product-handle="${product.handle}">
      <div class="wishlist-item-left">
        <a href="${product.url}" class="wishlist-item-img-link">
          <img src="${image}" alt="${product.title}" loading="lazy">
        </a>
      </div>
      <div class="wishlist-item-middle">
        <a href="${product.url}" class="wishlist-item-title">${product.title}</a>
        <div class="wishlist-item-price-container">
          <span class="wishlist-price">${price}</span>
          ${comparePrice ? `<span class="wishlist-compare-price">${comparePrice}</span>` : ''}
          ${discountBadge}
        </div>
      </div>
      <div class="wishlist-item-right">
        <button class="wishlist-remove-row-btn" data-wishlist-remove-handle="${product.handle}" aria-label="Remove from wishlist">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            <line x1="10" y1="11" x2="10" y2="17"></line>
            <line x1="14" y1="11" x2="14" y2="17"></line>
          </svg>
        </button>
      </div>
    </div>
  `;
}

function bindWishlistPageEvents() {
  const container = document.querySelector('[data-wishlist-container]');
  if (!container) return;

  container.querySelectorAll('[data-wishlist-remove-handle]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const handle = btn.getAttribute('data-wishlist-remove-handle');
      if (!handle) return;

      const index = window.GoAIWA.wishlist.indexOf(handle);
      if (index > -1) {
        window.GoAIWA.wishlist.splice(index, 1);
        localStorage.setItem('goaiwa_wishlist', JSON.stringify(window.GoAIWA.wishlist));
        updateWishlistCount();

        // Sync to account if logged in
        if (window.isCustomerLoggedIn) {
          syncWishlistToCustomerAccount();
        }

        // Animate removal
        const row = btn.closest('.wishlist-item-row');
        if (row) {
          row.style.opacity = '0';
          row.style.transform = 'translateY(-10px)';
          row.style.transition = 'all 0.3s ease';
          setTimeout(() => {
            row.remove();
            
            // Update counter in header
            const headerCount = container.querySelector('.wishlist-header-row h3');
            if (headerCount) {
              headerCount.textContent = `My Wishlist (${window.GoAIWA.wishlist.length})`;
            }

            if (window.GoAIWA.wishlist.length === 0) {
              container.innerHTML = '<div class="wishlist-empty-state"><p>Your wishlist is currently empty.</p><a href="/collections/all" class="button button--primary">Continue Shopping</a></div>';
            }
          }, 300);
        }
      }
    });
  });
}



// 8. MOBILE PRODUCT CARD TAP FLIP & NAVIGATE INTERACTION
function initMobileProductCardTap() {
  let touchStartX = 0;
  let touchStartY = 0;
  let isSwiping = false;

  document.addEventListener('touchstart', function(e) {
    if (e.touches && e.touches.length === 1) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = false;
    }
  }, { passive: true });

  document.addEventListener('touchmove', function(e) {
    if (e.touches && e.touches.length === 1) {
      let deltaX = Math.abs(e.touches[0].clientX - touchStartX);
      let deltaY = Math.abs(e.touches[0].clientY - touchStartY);
      if (deltaY > 25 || deltaX > 25) {
        isSwiping = true;
      }
    }
  }, { passive: true });

  // Event Delegation for click/tap on product image
  document.addEventListener('click', function(e) {
    const actionBtn = e.target.closest('.card-action-btn, .card-wishlist-btn');
    if (actionBtn) return; // Allow wishlist or cart buttons to execute naturally

    const imgContainer = e.target.closest('.bs-product-image');
    if (!imgContainer) {
      // Tapping outside resets active cards
      document.querySelectorAll('.bs-product-card.touch-active, .bs-product-card.mobile-flipped, .bs-product-image.mobile-flipped, .bs-product-image.show-secondary').forEach(el => {
        el.classList.remove('touch-active');
        el.classList.remove('mobile-flipped');
        el.classList.remove('show-secondary');
        const pImg = el.querySelector('img.bs-img-primary');
        const sImg = el.querySelector('img.bs-img-secondary');
        if (pImg) { pImg.style.removeProperty('opacity'); pImg.style.removeProperty('visibility'); }
        if (sImg) { sImg.style.removeProperty('opacity'); sImg.style.removeProperty('visibility'); sImg.style.removeProperty('z-index'); }
      });
      return;
    }

    if (isSwiping) {
      isSwiping = false;
      return;
    }

    const card = imgContainer.closest('.bs-product-card, .fd-product-card, .pfs-product-card');
    const cardUrl = imgContainer.getAttribute('data-product-url') || card?.querySelector('.bs-product-title')?.getAttribute('href') || card?.querySelector('a')?.getAttribute('href');

    // On mobile screens (< 769px)
    if (window.innerWidth < 769) {
      const hasSecondImage = imgContainer.classList.contains('has-second-image') || imgContainer.querySelector('img.bs-img-secondary') !== null;
      console.log('[GoAIWA Mobile Tap]', { imgContainer, hasSecondImage, isSwiping });

      if (hasSecondImage) {
        const isFlipped = imgContainer.classList.contains('mobile-flipped') || imgContainer.classList.contains('show-secondary') || (card && card.classList.contains('mobile-flipped'));

        if (!isFlipped) {
          // FIRST TAP ON 2-IMAGE PRODUCT:
          // 1. Keep product card in place (do NOT navigate)
          // 2. Flip product image from Image 1 -> Image 2
          // 3. Show Envelope (Cart) and Eye (Quick View) action buttons
          e.preventDefault();
          e.stopPropagation();

          console.log('[GoAIWA Mobile Tap] Flipping to 2nd image and showing action buttons');

          // Clear active state from other cards
          document.querySelectorAll('.bs-product-card.touch-active, .bs-product-card.mobile-flipped, .bs-product-image.mobile-flipped, .bs-product-image.show-secondary').forEach(el => {
            el.classList.remove('touch-active');
            el.classList.remove('mobile-flipped');
            el.classList.remove('show-secondary');
            const pImg = el.querySelector('img.bs-img-primary');
            const sImg = el.querySelector('img.bs-img-secondary');
            if (pImg) { pImg.style.removeProperty('opacity'); pImg.style.removeProperty('visibility'); }
            if (sImg) { sImg.style.removeProperty('opacity'); sImg.style.removeProperty('visibility'); sImg.style.removeProperty('z-index'); }
          });

          imgContainer.classList.add('mobile-flipped');
          imgContainer.classList.add('show-secondary');
          if (card) {
            card.classList.add('mobile-flipped');
            card.classList.add('touch-active');
            card.classList.add('show-secondary');
          }

          // Reveal secondary image on top at z-index: 5, keeping primary image visible underneath at z-index: 1
          const secondaryImg = imgContainer.querySelector('img.bs-img-secondary') || imgContainer.querySelectorAll('img')[1];

          if (secondaryImg) {
            secondaryImg.style.setProperty('opacity', '1', 'important');
            secondaryImg.style.setProperty('visibility', 'visible', 'important');
            secondaryImg.style.setProperty('z-index', '5', 'important');
          }
          return false;
        } else {
          console.log('[GoAIWA Mobile Tap] 2nd tap detected - navigating to product page:', cardUrl);
          // SECOND TAP: Navigate to product detail page
          if (cardUrl) {
            window.location.href = cardUrl;
          }
        }
      } else {
        // Single Image Product: Direct navigation on 1st tap
        if (cardUrl) {
          window.location.href = cardUrl;
        }
      }
    } else {
      // Desktop: Direct navigation on click
      if (cardUrl) {
        window.location.href = cardUrl;
      }
    }
  });
}
